/**
 * Azure Speech SDK Logger
 *
 * Unified structured logging for all Azure Speech SDK files (native, web, JS wrappers).
 * Uses the app's Logger internally for Sentry breadcrumbs and structured output.
 *
 * 4 methods, 2 layers:
 * - rnEvent / rnAnomaly — JS layer (AzureSpeech.ts, ConversationSpeech.ts)
 * - sdkEvent / sdkAnomaly — Native/SDK layer (natives, NativeAzureSpeech.web.ts)
 *
 * Controlled by two flags:
 * - logRN: enables JS-layer logging
 * - logSDK: enables Native/SDK-layer logging
 *
 * Event methods accept a phase parameter:
 * - 'start' (▶) — beginning of operation/event
 * - 'end' (■) — completion of operation/event
 * - null/undefined — point-in-time occurrence
 */

import { createLogger } from '../utils/logger';

type LogData = Record<string, unknown>;
type Phase = 'start' | 'end' | null;

const PHASE_MARKER = { start: '▶', end: '■' } as const;

function fmtTag(tag: string, phase?: Phase | null): string {
  if (phase) return `${PHASE_MARKER[phase]} ${tag}`;
  return tag;
}

export class SpeechLogger {
  private log: ReturnType<typeof createLogger>;
  private logRN: boolean;
  private logSDK: boolean;

  constructor(
    log: ReturnType<typeof createLogger>,
    logRN: boolean,
    logSDK: boolean,
  ) {
    this.log = log;
    this.logRN = logRN;
    this.logSDK = logSDK;
  }

  /** JS-layer normal flow (controlled by logRN) */
  rnEvent(tag: string, phase?: Phase | null, data?: LogData): void {
    if (!this.logRN) return;
    this.log.info(fmtTag(tag, phase), data);
  }

  /** JS-layer unexpected situation (always fires — feeds Sentry) */
  rnAnomaly(tag: string, errorOrData?: unknown, data?: LogData): void {
    this.log.warn(`⚠ ${tag}`, errorOrData, data);
  }

  /** Native/SDK-layer normal flow (controlled by logSDK) */
  sdkEvent(tag: string, phase?: Phase | null, data?: LogData): void {
    if (!this.logSDK) return;
    this.log.info(`SDK ${fmtTag(tag, phase)}`, data);
  }

  /** Native/SDK-layer unexpected situation (always fires — feeds Sentry) */
  sdkAnomaly(tag: string, errorOrData?: unknown, data?: LogData): void {
    this.log.warn(`SDK ⚠ ${tag}`, errorOrData, data);
  }
}
